import { useState, useEffect, useRef } from "react";
import { collection, onSnapshot, getDocs, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";

import BusinessesTab from "../businesses/BusinessesTab";
import ClientEmailsTab from "./ClientEmailsTab";
import InvoiceTab from "../invoices/InvoiceTab";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import StatusProgress from "../ui/StatusProgress";
import ClientModal from "./ClientModal";
import NewTaskModal from "../workflow/NewTaskModal";
import TaskModal from "../workflow/TaskModal";
import { BRAND, THEMES, STAFF, STAFF_COLORS, STATUS_FLOW, STATUS_META } from "../../config/constants";
import { generateEngagementLetterPDF } from "../../engagementPDF";
import { db, auth, functions } from "../../firebase";
import useMobile from "../../hooks/useMobile";
import { getDefaultTaxYear, formatPhone } from "../../utils/helpers";
import { qbCall } from "../../utils/api";
import { sp, tp, staffAvatar, btnP, btnS } from "../../utils/styles";

const storage = getStorage();

export default function ClientDetail({client,onClose,onUpdate,onDelete,theme,staffSigs={},allClients=[],currentUser=""}){
  const t=THEMES[theme];
  const mobile=useMobile();
  const [tab,setTab]=useState("overview");
  const [sending,setSending]=useState(false);
  const [sendStep,setSendStep]=useState(""); // progress label during send
  const [cancelling,setCancelling]=useState(false);
  const [showNewTask,setShowNewTask]=useState(false);
  const [showEdit,setShowEdit]=useState(false);
  const [openTask,setOpenTask]=useState(null);
  const [clientBusinesses,setClientBusinesses]=useState([]);
  const [oneDriveFolder,setOneDriveFolder]=useState(null);
  const [clientTasks,setClientTasks]=useState([]);
  const [showUpload,setShowUpload]=useState(false);
  const [subFolders,setSubFolders]=useState([]);
  const [spOpen,setSpOpen]=useState(false);
  const [spFolderFiles,setSpFolderFiles]=useState({});
  const [spOpenFolder,setSpOpenFolder]=useState(null);
  const [spLoadingFolder,setSpLoadingFolder]=useState(null);
  const [newFolderName,setNewFolderName]=useState("");
  const [creatingFolder,setCreatingFolder]=useState(false);
  const [createFolderMsg,setCreateFolderMsg]=useState("");

  // Load OneDrive linked folder for this client
  useEffect(()=>{
    if(!client?.id) return;
    getDocs(collection(db,"onedrive_links")).then(snap=>{
      const match=snap.docs.find(d=>d.data().clientId===client.id);
      if(match) setOneDriveFolder({folderId:match.id,...match.data()});
    }).catch(()=>{});
  },[client.id]);

  // Load active tasks for this client (including tasks linked to their businesses)
  useEffect(()=>{
    if(!client?.id) return;
    const bizIds = clientBusinesses.map(b=>b.id);
    const unsub=onSnapshot(collection(db,"tasks"),snap=>{
      const all=snap.docs.map(d=>({id:d.id,...d.data()}));
      setClientTasks(all.filter(tk=>{
        if(tk.status==="closed"||tk.status==="resolved") return false;
        if(tk.clientId===client.id) return true;
        if(tk.businessId && bizIds.includes(tk.businessId)) return true;
        return false;
      }).sort((a,b)=>(b.updatedAt?.seconds||0)-(a.updatedAt?.seconds||0)));
    });
    return unsub;
  },[client.id, clientBusinesses]);

  // Load businesses from collection (filter client-side)
  useEffect(()=>{
    if(!client?.id) return;
    const unsub=onSnapshot(collection(db,"businesses"),snap=>{
      const all=snap.docs.map(d=>({id:d.id,...d.data()}));
      setClientBusinesses(all.filter(b=>b.ownerId===client.id));
    });
    return unsub;
  },[client.id]);
  const [genPDF,setGenPDF]=useState(false);
  const [esignError,setEsignError]=useState("");
  const [uploadProgress,setUploadProgress]=useState(null);

  // Auto-refresh QB invoices in background when client profile opens
  useEffect(()=>{
    if(!client?.id || !client.qbCustomerId) return;
    qbCall("qbManualSyncInvoices",{crmClientId:client.id}).catch(()=>{});
  },[client.id]);
  const [uploadError,setUploadError]=useState("");
  const [uploadSuccess,setUploadSuccess]=useState("");
  const fileInputRef=useRef();
  // Load sub-folders: Firestore cache first (instant), then MS Graph sync in background
  const loadSubFolders=async()=>{
    if(!oneDriveFolder) return;
    // 1. Instant: load from Firestore cache
    try{
      const cacheSnap=await getDocs(collection(db,"onedrive_subfolders"));
      const cached=cacheSnap.docs.find(d=>d.id===oneDriveFolder.folderId);
      if(cached&&cached.data().folders?.length){
        setSubFolders(cached.data().folders);
      }
    }catch(e){}
    // 2. Background: sync from MS Graph and update cache
    try{
      const token=await auth.currentUser?.getIdToken();
      const res=await fetch("https://us-central1-netsave-crm.cloudfunctions.net/msListSubFolders",{
        method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token},
        body:JSON.stringify({folderId:oneDriveFolder.folderId})
      });
      const data=await res.json();
      const fresh=data.folders||[];
      setSubFolders(fresh);
      // Cache to Firestore for next time
      try{ await setDoc(doc(db,"onedrive_subfolders",oneDriveFolder.folderId),{folders:fresh,updatedAt:serverTimestamp()},{merge:true}); }catch(e){}
    }catch(e){console.warn("loadSubFolders sync:",e.message);}
  };

  const loadFolderFiles=async(folderId)=>{
    // 1. Instant: load from Firestore cache
    try{
      const cacheSnap=await getDocs(collection(db,"onedrive_files_cache"));
      const cached=cacheSnap.docs.find(d=>d.id===folderId);
      if(cached&&cached.data().items?.length){
        setSpFolderFiles(prev=>({...prev,[folderId]:cached.data().items}));
      }
    }catch(e){}
    // 2. Background: sync from MS Graph
    setSpLoadingFolder(folderId);
    try{
      const token=await auth.currentUser?.getIdToken();
      const res=await fetch("https://us-central1-netsave-crm.cloudfunctions.net/msFetchOneDriveFolder?folderId="+folderId,{
        method:"GET",headers:{Authorization:"Bearer "+token}
      });
      const data=await res.json();
      const fresh=data.items||[];
      setSpFolderFiles(prev=>({...prev,[folderId]:fresh}));
      // Cache to Firestore
      try{ await setDoc(doc(db,"onedrive_files_cache",folderId),{items:fresh.map(f=>({id:f.id,name:f.name,size:f.size||0,webUrl:f.webUrl||"",folder:!!f.folder})),updatedAt:serverTimestamp()},{merge:true}); }catch(e){}
    }catch(e){console.warn("loadFolderFiles sync:",e.message);}
    setSpLoadingFolder(null);
  };

  const handleCreateSubFolder=async()=>{
    if(!newFolderName.trim()||!oneDriveFolder) return;
    setCreatingFolder(true);setCreateFolderMsg("");
    try{
      const token=await auth.currentUser?.getIdToken();
      const parentId=spOpenFolder||oneDriveFolder.folderId;
      const res=await fetch("https://us-central1-netsave-crm.cloudfunctions.net/msCreateFolder",{
        method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token},
        body:JSON.stringify({parentFolderId:parentId,folderName:newFolderName.trim()})
      });
      const data=await res.json();
      if(res.status===409||data.exists){
        setCreateFolderMsg("⚠ Folder already exists");
      } else if(!res.ok){
        throw new Error(data.error||"Failed");
      } else {
        setCreateFolderMsg("✓ Created: "+data.name);
        setNewFolderName("");
        loadSubFolders();
        if(spOpenFolder) loadFolderFiles(spOpenFolder);
      }
    }catch(e){setCreateFolderMsg("Error: "+e.message);}
    setCreatingFolder(false);
  };

  // Engagement PDF options — which businesses + bookkeeping to include
  const allBizIds=clientBusinesses.map((_,i)=>i);
  const [includedBiz,setIncludedBiz]=useState(()=>new Set(allBizIds));
  const [bizBookkeeping,setBizBookkeeping]=useState(()=>new Set(allBizIds.filter(i=>client.isBookkeeping)));
  const toggleBiz=(i)=>setIncludedBiz(prev=>{const n=new Set(prev);n.has(i)?n.delete(i):n.add(i);return n;});
  const toggleBizBookkeeping=(i)=>setBizBookkeeping(prev=>{const n=new Set(prev);n.has(i)?n.delete(i):n.add(i);return n;});

  // Build the client object that gets passed to the PDF generator
  const pdfClient=()=>({
    ...client,
    businesses:clientBusinesses.reduce((acc,biz,i)=>{
      if(!includedBiz.has(i)) return acc;
      return [...acc,{...biz,includeBookkeeping:bizBookkeeping.has(i)}];
    },[]),
    isBookkeeping:false,
  });

  const handleSend=async(isResend=false)=>{
    setSending(true);setEsignError("");setSendStep("Generating PDF...");
    try{
      const pdfBlob=await generateEngagementLetterPDF(pdfClient(),{download:false,returnBlob:true},staffSigs);
      setSendStep("Uploading to DocuSeal...");
      const base64=await new Promise((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=()=>resolve(reader.result.split(",")[1]);
        reader.onerror=reject;
        reader.readAsDataURL(pdfBlob);
      });
      const safeName=(client.name||"client").replace(/[^a-z0-9]/gi,"_");
      const filename=`Engagement_Letter_${safeName}_${client.taxYear||getDefaultTaxYear()}.pdf`;
      setSendStep("Creating signing request...");
      const sendFn=httpsCallable(functions,"sendEngagementLetter",{timeout:120000});
      const result=await sendFn({pdfBase64:base64,filename,client});
      setSendStep("Sending emails...");
      const submissionId = result.data?.submissionId || null;
      await onUpdate({
        ...client,
        status: "Engagement Sent",
        engagementSent: true,
        ...(submissionId ? { docusealSubmissionId: submissionId } : {}),
      });
      setSendStep("");
    }catch(e){
      setEsignError((isResend?"Resend":"Send")+" failed: "+(e?.details||e?.message||"Unknown error"));
      setSendStep("");
    }
    setSending(false);
  };

  const handleCancel=async()=>{
    if(!window.confirm("Cancel this signing request? The client will no longer be able to sign.")) return;
    setCancelling(true);
    try{
      await onUpdate({...client,engagementSent:false,docusealSubmissionId:null,status:"Not Started"});
    }catch(e){ console.error("Cancel failed:",e.message); }
    setCancelling(false);
  };

  // ── Start New Tax Year ────────────────────────────────────────────────────
  const [startingYear,setStartingYear]=useState(false);
  const [yearConfirm,setYearConfirm]=useState("");
  const nextYear=String(parseInt(client.taxYear||getDefaultTaxYear())+1);
  // Lockout: button disabled before Oct 19
  const now=new Date();
  const rolloverUnlocked=now.getMonth()>9||(now.getMonth()===9&&now.getDate()>=19);

  const handleStartNewYear=async()=>{
    if(yearConfirm!==nextYear){alert(`Please type ${nextYear} to confirm.`);return;}
    setStartingYear(true);
    try{
      // Call Cloud Function — enforces Oct 19 lockout server-side too
      const rolloverFn=httpsCallable(functions,"rolloverClientYear");
      await rolloverFn({clientId:client.id});
      setYearConfirm("");
      // onUpdate will re-fetch — just close the confirm input
    }catch(e){
      alert("Rollover failed: "+(e?.message||"Unknown error"));
    }
    setStartingYear(false);
  };

  const handlePDF=async()=>{
    setGenPDF(true);
    try{await generateEngagementLetterPDF(pdfClient(),{download:true,returnBlob:false},staffSigs);}
    catch(e){alert("PDF error: "+e.message);}
    setGenPDF(false);
  };

  const handleTaxReturnUpload=async(e)=>{
    const file=e.target.files?.[0];
    if(!file) return;
    setUploadError("");setUploadSuccess("");
    if(!file.name.toLowerCase().endsWith(".pdf")){setUploadError("Only PDF files are accepted.");return;}
    const year=client.taxYear||getDefaultTaxYear();
    const storagePath=`tax-returns/${year}/${file.name}`;
    const storageRef=ref(storage,storagePath);
    const uploadTask=uploadBytesResumable(storageRef,file);
    setUploadProgress(0);
    uploadTask.on("state_changed",
      snap=>{setUploadProgress(Math.round((snap.bytesTransferred/snap.totalBytes)*100));},
      err=>{setUploadError("Upload failed: "+err.message);setUploadProgress(null);},
      async()=>{
        setUploadProgress(100);
        setUploadSuccess(`"${file.name}" uploaded. Matching to client...`);
        setTimeout(()=>{setUploadProgress(null);setUploadSuccess("Tax return uploaded and matched ✓");},2000);
      }
    );
    if(fileInputRef.current) fileInputRef.current.value="";
  };

  const taxReturnUrl=client[`taxReturnUrl_${client.taxYear||getDefaultTaxYear()}`];
  const taxReturnFile=client[`taxReturnFile_${client.taxYear||getDefaultTaxYear()}`];

  return(
    <div>
        {showNewTask&&<NewTaskModal currentUser={currentUser} onClose={()=>setShowNewTask(false)} theme={theme} clients={allClients} prefillClient={client}/>}
        {showEdit&&<ClientModal client={client} onSave={async(data)=>{await onUpdate(data);setShowEdit(false);}} onClose={()=>setShowEdit(false)} theme={theme}/>}
        {openTask&&<TaskModal task={openTask} currentUser={currentUser} onClose={()=>setOpenTask(null)} theme={theme} clients={allClients}/>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 0",marginTop:4,display:"flex",alignItems:"center"}}><Icon name="arrow" size={18} color={t.textSub}/></button>
            <div>
              <div style={{fontSize:20,fontWeight:800,color:t.text,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                <select value={client.prefix||""} onChange={e=>onUpdate({...client,prefix:e.target.value})} style={{padding:"2px 4px",borderRadius:6,border:"1px solid "+t.border,background:t.bg,color:t.textMuted,fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif",cursor:"pointer",minWidth:client.prefix?undefined:50}}>
                  <option value="">—</option><option>Mr.</option><option>Mrs.</option><option>Ms.</option><option>Miss</option><option>Dr.</option>
                </select>
                {client.name}{client.spouseName?<span style={{fontSize:14,fontWeight:500,color:t.textSub}}> & {client.spouseName}</span>:""}</div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <span style={tp(client.type,theme)}><Icon name={client.type==="Individual"?"user":"building"} size={10} color={client.type==="Individual"?BRAND.blue:BRAND.green}/>{client.type}</span>
                <span style={sp(client.status,theme)}>{client.status}</span>
                <span style={{fontSize:11.5,color:t.textMuted}}>Tax Year {client.taxYear}</span>
                {/* Year tags */}
                {(client.activeYears||[client.taxYear]).filter(Boolean).sort().map(y=>(
                  <span key={y} style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:8,
                    background:y===client.taxYear?BRAND.blue+"20":"transparent",
                    border:`1px solid ${y===client.taxYear?BRAND.blue+"50":t.border}`,
                    color:y===client.taxYear?BRAND.blue:t.textMuted}}>{y}</span>
                ))}
                <div style={{display:"flex",alignItems:"center",gap:5}}>{staffAvatar(client.assignedTo,20)}<span style={{fontSize:11.5,color:t.textSub,fontWeight:600}}>{client.assignedTo}</span></div>
              </div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            {oneDriveFolder&&<button onClick={()=>{setSpOpen(!spOpen);if(!spOpen){loadSubFolders();setSpOpenFolder(null);}}} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:8,background:spOpen?"#0078d420":"#0078d410",border:"1px solid #0078d430",cursor:"pointer",color:"#0078d4",fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}>📁 SharePoint{spOpen?" ▾":" ▸"}</button>}
            {!oneDriveFolder&&<button onClick={async()=>{try{setCreateFolderMsg("Creating...");const token=await auth.currentUser?.getIdToken();const res=await fetch("https://us-central1-netsave-crm.cloudfunctions.net/msCreateClientFolder",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token},body:JSON.stringify({clientId:client.id,clientName:client.name,taxYear:client.taxYear||getDefaultTaxYear()})});const data=await res.json();if(data.success&&data.folderId){setOneDriveFolder({folderId:data.folderId,folderName:data.folderName,webUrl:data.webUrl});setCreateFolderMsg("");}else if(data.error&&data.error.includes("exists")){setCreateFolderMsg("⚠ Folder already exists in SharePoint. Go to OneDrive tab to link it.");}}catch(e){setCreateFolderMsg("Error: "+e.message);}}} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:8,background:"#0078d410",border:"1px solid #0078d430",cursor:"pointer",color:"#0078d4",fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}>📁 Create SP Folder</button>}
            {currentUser&&<button onClick={()=>setShowEdit(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:8,background:BRAND.green+"15",border:`1px solid ${BRAND.green}30`,cursor:"pointer",color:BRAND.green,fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}><Icon name="edit" size={13} color={BRAND.green}/>Edit</button>}
            {currentUser&&<button onClick={()=>setShowNewTask(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:8,background:BRAND.blue+"15",border:`1px solid ${BRAND.blue}30`,cursor:"pointer",color:BRAND.blue,fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}>📋 Create Task</button>}
            {onDelete&&<button onClick={()=>onDelete(client)} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:8,background:"transparent",border:"1px solid #f8717130",cursor:"pointer",color:"#f87171",fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}><Icon name="trash" size={13} color="#f87171"/>Delete</button>}
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",padding:4}}><Icon name="x" size={18} color={t.textSub}/></button>
          </div>
        </div>
        {createFolderMsg&&!oneDriveFolder&&<div style={{padding:"10px 16px",marginBottom:10,borderRadius:8,background:createFolderMsg.startsWith("⚠")?"#fef3c7":createFolderMsg.startsWith("Error")?"#fef2f2":"#f0fdf4",border:"1px solid "+(createFolderMsg.startsWith("⚠")?"#f59e0b40":createFolderMsg.startsWith("Error")?"#f8717140":"#8BC44240"),fontSize:12.5,color:createFolderMsg.startsWith("⚠")?"#92400e":createFolderMsg.startsWith("Error")?"#dc2626":"#166534",fontWeight:600}}>{createFolderMsg}</div>}

        {/* ── SharePoint Inline File Browser ── */}
        {spOpen&&oneDriveFolder&&(
          <div style={{background:t.surface,border:"1px solid #0078d425",borderRadius:12,marginBottom:14,overflow:"hidden"}}>
            {/* Header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 18px",background:"#0078d408",borderBottom:"1px solid #0078d420"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16}}>📁</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:t.text}}>{oneDriveFolder.folderName||client.name}</div>
                  <div style={{fontSize:11,color:t.textMuted}}>{subFolders.length} sub-folder{subFolders.length!==1?"s":""}</div>
                </div>
              </div>
              <div style={{display:"flex",gap:6}}>
                {(oneDriveFolder.webUrl||oneDriveFolder.sharePointUrl)&&<a href={oneDriveFolder.webUrl||oneDriveFolder.sharePointUrl} target="_blank" rel="noopener noreferrer" style={{padding:"5px 10px",borderRadius:7,background:"transparent",border:"1px solid #0078d430",color:"#0078d4",fontSize:11,fontWeight:600,textDecoration:"none",fontFamily:"'Outfit',sans-serif"}}>↗ Open in SharePoint</a>}
                <button onClick={()=>setSpOpen(false)} style={{background:"none",border:"none",cursor:"pointer",padding:2}}><Icon name="x" size={14} color={t.textMuted}/></button>
              </div>
            </div>

            {/* Sub-folders list */}
            <div style={{padding:"0"}}>
              {subFolders.length===0&&<div style={{padding:"20px 18px",textAlign:"center",color:t.textMuted,fontSize:13}}>Loading folders...</div>}
              {subFolders.map(f=>{
                const isOpen=spOpenFolder===f.id;
                const files=spFolderFiles[f.id]||[];
                const loading=spLoadingFolder===f.id;
                return(
                  <div key={f.id}>
                    <div onClick={()=>{if(isOpen){setSpOpenFolder(null);}else{setSpOpenFolder(f.id);setUploadTarget(f.id);if(!spFolderFiles[f.id])loadFolderFiles(f.id);}}}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"10px 18px",cursor:"pointer",borderBottom:"1px solid "+t.border,transition:"background 0.1s",background:isOpen?"#0078d408":"transparent"}}
                      onMouseEnter={e=>{if(!isOpen)e.currentTarget.style.background=t.bg;}} onMouseLeave={e=>{if(!isOpen)e.currentTarget.style.background="transparent";}}>
                      <span style={{fontSize:14}}>{isOpen?"📂":"📁"}</span>
                      <div style={{flex:1,fontSize:13,fontWeight:600,color:t.text}}>{f.name}</div>
                      {f.webUrl&&<a href={f.webUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:11,color:"#0078d4",textDecoration:"none",fontWeight:600,padding:"2px 8px",borderRadius:6,border:"1px solid #0078d425"}}>↗ Open</a>}
                      <span style={{fontSize:11,color:t.textMuted}}>{isOpen?"▾":"▸"}</span>
                    </div>
                    {isOpen&&(
                      <div style={{background:t.bg,borderBottom:"1px solid "+t.border}}>
                        {loading&&<div style={{padding:"12px 28px",fontSize:12,color:t.textMuted}}>Loading files...</div>}
                        {!loading&&files.length===0&&<div style={{padding:"12px 28px",fontSize:12,color:t.textMuted,fontStyle:"italic"}}>Empty folder</div>}
                        {!loading&&files.map(file=>(
                          <div key={file.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 28px",fontSize:12}}>
                            <span>{file.folder?"📁":"📄"}</span>
                            <div style={{flex:1,color:t.text,fontWeight:500}}>{file.name}</div>
                            <span style={{color:t.textMuted,fontSize:11}}>{file.size?(file.size/1024).toFixed(0)+"KB":""}</span>
                            {file.webUrl&&<a href={file.webUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"#0078d4",textDecoration:"none",fontWeight:600}}>↗ Open</a>}
                          </div>
                        ))}
                        {/* Upload to this folder */}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Create new folder + upload to root */}
            <div style={{padding:"14px 18px",borderTop:"1px solid "+t.border,background:"#0078d405"}}>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <input value={newFolderName} onChange={e=>setNewFolderName(e.target.value)} placeholder="New folder name..." style={{flex:1,minWidth:140,padding:"7px 12px",borderRadius:8,border:"1px solid "+t.border,background:t.inputBg||t.bg,color:t.text,fontSize:13,fontFamily:"'Outfit',sans-serif",outline:"none"}}/>
                <button onClick={handleCreateSubFolder} disabled={!newFolderName.trim()||creatingFolder} style={{padding:"7px 16px",borderRadius:8,background:creatingFolder?"#ccc":"#0078d4",border:"none",color:"#fff",fontSize:12,fontWeight:700,cursor:creatingFolder?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif",opacity:!newFolderName.trim()?0.4:1}}>
                  {creatingFolder?"Creating...":"+ Create Folder"}
                </button>
              </div>
              {createFolderMsg&&oneDriveFolder&&<div style={{marginTop:8,fontSize:12,color:createFolderMsg.startsWith("Error")||createFolderMsg.startsWith("⚠")?"#f59e0b":"#0078d4",fontWeight:600}}>{createFolderMsg}</div>}
            </div>
          </div>
        )}

        <div style={{background:t.bg,borderRadius:12,padding:"12px 16px",marginBottom:12,border:`1px solid ${t.border}`}}>
          <div style={{fontSize:10,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Tax Process</div>
          <StatusProgress status={client.status} theme={theme}/>
          <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:10}}>
            {STATUS_FLOW.map(s=>{const m=STATUS_META[s];const active=client.status===s;return <button key={s} onClick={()=>onUpdate({...client,status:s})} style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${active?m.border+"60":t.border}`,background:active?m.bg(theme):t.bg,color:active?m.text:t.textMuted,fontFamily:"'Outfit',sans-serif"}}>{s}</button>;})}
          </div>
        </div>

        <div style={{background:t.bg,borderRadius:10,padding:"10px 14px",marginBottom:12,border:`1px solid ${t.border}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:10,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>Assigned To</div>
          <div style={{display:"flex",gap:8}}>
            {STAFF.map(s=><button key={s} onClick={()=>onUpdate({...client,assignedTo:s})} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,border:`1px solid ${client.assignedTo===s?STAFF_COLORS[s]+"60":t.border}`,background:client.assignedTo===s?STAFF_COLORS[s]+"18":"transparent",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>{staffAvatar(s,18)}<span style={{fontSize:11.5,fontWeight:600,color:client.assignedTo===s?STAFF_COLORS[s]:t.textSub}}>{s}</span></button>)}
          </div>
        </div>

        <div style={{display:"flex",gap:2,borderBottom:`1px solid ${t.border}`,marginBottom:18,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
          {["overview","engagement","businesses","invoice","emails","history"].map(tab2=><button key={tab2} onClick={()=>setTab(tab2)} style={{padding:"8px 14px",fontSize:12.5,fontWeight:600,cursor:"pointer",border:"none",borderBottom:tab===tab2?`2px solid ${BRAND.blue}`:"2px solid transparent",background:"transparent",color:tab===tab2?BRAND.blue:t.textMuted,textTransform:"capitalize",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",flexShrink:0}}>{tab2}</button>)}
        </div>

        {tab==="overview"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:mobile?"1fr 1fr":"1fr 1fr 1fr",gap:14,marginBottom:14}}>
              {[{l:"Email",v:client.email},{l:"Phone",v:formatPhone(client.phone)},{l:"Client Since",v:client.joined},{l:"Address",v:[client.address,client.city,client.state,client.zip].filter(Boolean).join(", ")||"—"}].map(f=><div key={f.l}><div style={{fontSize:10,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{f.l}</div><div style={{fontSize:12.5,color:t.text,fontWeight:500,wordBreak:"break-word"}}>{f.v||"—"}</div></div>)}
            </div>
            {client.type==="Individual"&&(
              <div style={{background:t.bg,borderRadius:10,padding:"14px 16px",border:`1px solid ${t.border}`}}>
                <div style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:12}}>Individual & Spouse</div>
                <div style={{display:"grid",gridTemplateColumns:mobile?"1fr 1fr":"1fr 1fr 1fr",gap:14}}>
                  <div><div style={{fontSize:10,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Spouse Prefix</div><select value={client.spousePrefix||""} onChange={e=>onUpdate({...client,spousePrefix:e.target.value})} style={{padding:"6px 10px",borderRadius:8,border:"1px solid "+t.border,background:t.bg,color:t.text,fontSize:13,fontFamily:"'Outfit',sans-serif",cursor:"pointer"}}><option value="">—</option><option>Mr.</option><option>Mrs.</option><option>Ms.</option><option>Miss</option><option>Dr.</option></select></div>
                  {[{l:"Spouse Name",v:client.spouseName},{l:"Spouse Email",v:client.spouseEmail}].map(f=><div key={f.l}><div style={{fontSize:10,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{f.l}</div><div style={{fontSize:12.5,color:t.text,fontWeight:500}}>{f.v||"—"}</div></div>)}
                </div>
              </div>
            )}
            {/* Active Tasks */}
            {clientTasks.length>0&&(
              <div style={{background:t.bg,borderRadius:10,padding:"14px 16px",border:`1px solid ${t.border}`,marginTop:14}}>
                <div style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:10}}>Active Tasks ({clientTasks.length})</div>
                {clientTasks.map(tk=>(
                  <div key={tk.id} onClick={()=>setOpenTask(tk)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,border:"1px solid "+t.border,marginBottom:6,background:t.surface,cursor:"pointer",transition:"background 0.1s"}}
                    onMouseEnter={e=>e.currentTarget.style.background=t.bg}
                    onMouseLeave={e=>e.currentTarget.style.background=t.surface}>
                    <span style={{fontSize:10,fontWeight:700,color:tk.status==="open"?"#f59e0b":tk.status==="completed"?BRAND.green:t.textMuted,background:(tk.status==="open"?"#f59e0b":tk.status==="completed"?BRAND.green:t.textMuted)+"18",padding:"2px 7px",borderRadius:6}}>{tk.status==="open"?"Open":tk.status==="completed"?"Done":"Pending"}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12.5,fontWeight:700,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tk.title}</div>
                      <div style={{fontSize:11,color:t.textMuted}}>{staffAvatar(tk.assignedTo,14)} {tk.assignedTo}{tk.createdBy!==tk.assignedTo?" ← "+tk.createdBy:""}{tk.businessName?" · 🏢 "+tk.businessName:""}</div>
                    </div>
                    {(tk.comments||[]).filter(c=>c.type!=="system").length>0&&<span style={{fontSize:10.5,color:t.textMuted}}>💬{(tk.comments||[]).filter(c=>c.type!=="system").length}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab==="engagement"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:13,color:t.textSub}}>Engagement letter eSignature via DocuSeal. Tax return upload & view.</div>
              <div style={{display:"inline-flex",alignItems:"center",gap:6,background:theme==="dark"?"#001a0a":"#f0fff4",border:`1px solid ${BRAND.green}40`,borderRadius:6,padding:"4px 10px",fontSize:11,color:BRAND.green,fontWeight:700}}>&#10003; DocuSeal eSign</div>
            </div>
            {esignError&&<div style={{background:"#fff0f0",border:"1px solid #f87171",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12.5,color:"#dc2626"}}>{esignError}</div>}

            {/* ── What to Include ───────────────────────────────────────── */}
            {clientBusinesses.length>0&&(
              <div style={{background:t.bg,borderRadius:10,padding:"14px 16px",border:`1px solid ${BRAND.blue}30`,marginBottom:14}}>
                <div style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:12}}>📋 What to Include in This PDF</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {clientBusinesses.map((biz,i)=>(
                    <div key={i}>
                      {/* Business row */}
                      <label onClick={()=>toggleBiz(i)} style={{display:"inline-flex",alignItems:"center",gap:8,padding:"7px 14px",borderRadius:20,border:`1.5px solid ${includedBiz.has(i)?BRAND.blue:t.border}`,background:includedBiz.has(i)?BRAND.blue+"12":"transparent",cursor:"pointer",userSelect:"none",transition:"all 0.15s"}}>
                        <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${includedBiz.has(i)?BRAND.blue:t.border}`,background:includedBiz.has(i)?BRAND.blue:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                          {includedBiz.has(i)&&<svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <Icon name="building" size={12} color={includedBiz.has(i)?BRAND.blue:t.textMuted}/>
                        <span style={{fontSize:12.5,fontWeight:600,color:includedBiz.has(i)?BRAND.blue:t.textSub}}>{biz.legalName||`Business ${i+1}`}</span>
                      </label>
                      {/* Nested bookkeeping — only if business is checked AND business has bookkeeping */}
                      {includedBiz.has(i)&&biz.isBookkeeping&&(
                        <div style={{marginLeft:32,marginTop:5}}>
                          <label onClick={()=>toggleBizBookkeeping(i)} style={{display:"inline-flex",alignItems:"center",gap:7,padding:"5px 12px",borderRadius:16,border:`1.5px solid ${bizBookkeeping.has(i)?BRAND.green:t.border}`,background:bizBookkeeping.has(i)?BRAND.green+"12":"transparent",cursor:"pointer",userSelect:"none",transition:"all 0.15s"}}>
                            <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${bizBookkeeping.has(i)?BRAND.green:t.border}`,background:bizBookkeeping.has(i)?BRAND.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                              {bizBookkeeping.has(i)&&<svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3L3 5.5L7 1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </div>
                            <span style={{fontSize:12}}>📒</span>
                            <span style={{fontSize:12,fontWeight:600,color:bizBookkeeping.has(i)?BRAND.green:t.textMuted}}>Include Bookkeeping</span>
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{background:t.bg,borderRadius:10,padding:16,border:`1px solid ${t.border}`,marginBottom:14}}>
              <div style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:12}}>Letter Content Preview</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                <div>
                  <div style={{fontSize:10.5,color:t.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.07em"}}>Scope of Engagement</div>
                  {(client.scopeOfEngagement||"").split("\n").filter(Boolean).map((line,i)=><div key={i} style={{fontSize:12.5,color:t.text,display:"flex",gap:7,marginBottom:5}}><span style={{color:BRAND.blue,marginTop:1}}>◦</span>{line}</div>)}
                  {!client.scopeOfEngagement&&<div style={{fontSize:12,color:t.textMuted,fontStyle:"italic"}}>Not set — edit client to add scope</div>}
                  {client.isBookkeeping&&<div style={{marginTop:8,display:"inline-flex",alignItems:"center",gap:5,background:BRAND.green+"15",border:`1px solid ${BRAND.green}40`,borderRadius:12,padding:"2px 9px",fontSize:11,color:BRAND.green,fontWeight:700}}>📒 Bookkeeping</div>}
                </div>
                <div>
                  <div style={{fontSize:10.5,color:t.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.07em"}}>Fee Schedule</div>
                  {(client.feeRows&&client.feeRows.length>0)
                    ?<table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
                        <tbody>
                          {client.feeRows.map((row,i)=>(
                            <tr key={i} style={{borderBottom:`1px solid ${t.border}`}}>
                              <td style={{padding:"5px 0",color:t.text,fontWeight:600}}>{row.label||"—"}</td>
                              <td style={{padding:"5px 0",color:BRAND.green,fontWeight:800,textAlign:"right",whiteSpace:"nowrap"}}>{row.amount||"—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    :<div style={{fontSize:12,color:t.textMuted,fontStyle:"italic"}}>Not set — edit client to add fees</div>
                  }
                  {client.spouseName&&<div style={{marginTop:10}}><div style={{fontSize:10.5,color:t.textMuted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.07em"}}>Signature Recipients</div><div style={{fontSize:12.5,color:t.text}}>{client.email}</div>{client.spouseEmail&&<div style={{fontSize:12.5,color:t.text}}>{client.spouseEmail}</div>}</div>}
                </div>
              </div>
            </div>
            <div style={{background:t.bg,borderRadius:12,padding:20,border:`1px solid ${t.border}`,marginBottom:14}}>
              {/* Header row — title + status badge */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
                <div style={{fontWeight:700,fontSize:14,color:t.text}}>Engagement Letter — {client.taxYear}</div>
                {client.engagementSigned
                  ? <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:BRAND.green+"20",color:BRAND.green,border:`1px solid ${BRAND.green}40`}}>
                      <Icon name="check" size={10} color={BRAND.green}/>Signed
                    </span>
                  : client.engagementSent
                  ? <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:BRAND.blue+"15",color:BRAND.blue,border:`1px solid ${BRAND.blue}30`}}>
                      <Icon name="spinner" size={10} color={BRAND.blue}/>Awaiting Signature
                    </span>
                  : <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:t.surfaceAlt||t.bg,color:t.textMuted,border:`1px solid ${t.border}`}}>
                      Not Sent
                    </span>
                }
              </div>

              {/* Buttons */}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                {/* Preview PDF — always */}
                <button onClick={handlePDF} disabled={genPDF} style={{...btnP,background:`linear-gradient(135deg,${BRAND.green},#6fa830)`}}>
                  {genPDF?<><Icon name="spinner" size={13} color="#fff"/>Generating PDF...</>:<><Icon name="download" size={13} color="#fff"/>Preview PDF</>}
                </button>

                {/* STATE 1 — Not sent: show Send */}
                {!client.engagementSent&&!client.engagementSigned&&(
                  <button onClick={()=>handleSend(false)} disabled={sending||!client.email}
                    style={{...btnP,opacity:!client.email?0.5:1,cursor:!client.email?"not-allowed":"pointer"}}
                    title={!client.email?"Add client email first":""}>
                    <Icon name="mail" size={13} color="#fff"/>Send for Signature
                  </button>
                )}

                {/* STATE 2 — Sent, awaiting: show Resend + Cancel + Mark Signed */}
                {client.engagementSent&&!client.engagementSigned&&(<>
                  <button onClick={()=>handleSend(true)} disabled={sending||cancelling}
                    style={{...btnS(t),display:"flex",alignItems:"center",gap:6}}>
                    {sending?<><Icon name="spinner" size={13} color={t.text}/>Resending...</>:<><Icon name="arrow" size={13}/>Resend</>}
                  </button>
                  <button onClick={handleCancel} disabled={cancelling||sending}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",borderRadius:8,
                      background:"#fff0f0",border:"1px solid #f8717150",color:"#dc2626",
                      fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                    {cancelling?<><Icon name="spinner" size={13} color="#dc2626"/>Cancelling...</>:<><Icon name="trash" size={13} color="#dc2626"/>Cancel Request</>}
                  </button>
                  <button onClick={()=>onUpdate({...client,engagementSigned:true,status:"Engagement Signed"})}
                    style={{...btnP,background:`linear-gradient(135deg,${BRAND.green},#6fa830)`}}>
                    <Icon name="check" size={13} color="#fff"/>Mark as Signed
                  </button>
                </>)}

                {/* STATE 3 — Signed: show View Signed Copy */}
                {client.engagementSigned&&(
                  client.signedEngagementUrl
                    ? <a href={client.signedEngagementUrl} target="_blank" rel="noopener noreferrer"
                        style={{...btnP,background:"transparent",border:`1px solid ${BRAND.blue}40`,color:BRAND.blue,textDecoration:"none"}}>
                        <Icon name="eye" size={13} color={BRAND.blue}/>View Signed Copy
                      </a>
                    : <span style={{fontSize:12,color:t.textMuted,fontStyle:"italic"}}>Signed — document link pending</span>
                )}
              </div>

              {/* Error message */}
              {esignError&&<div style={{marginTop:10,background:"#fff0f0",border:"1px solid #f8717140",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#dc2626"}}>{esignError}</div>}

              {/* Submission ID — only when awaiting */}
              {client.docusealSubmissionId&&!client.engagementSigned&&(
                <div style={{marginTop:8,fontSize:11,color:t.textMuted}}>
                  Submission ID: <span style={{fontFamily:"monospace",color:t.textSub}}>{client.docusealSubmissionId}</span>
                </div>
              )}

              {/* Sending overlay — bouncing bars */}
              {sending&&<div style={{position:"fixed",inset:0,zIndex:9999,background:theme==="dark"?"rgba(0,0,0,0.75)":"rgba(255,255,255,0.9)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20}}>
                <div style={{display:"flex",gap:7,alignItems:"flex-end",height:44}}>
                  {[{c:BRAND.blue,d:"0s"},{c:BRAND.blue+"cc",d:"0.15s"},{c:BRAND.green,d:"0.3s"},{c:BRAND.green+"cc",d:"0.45s"},{c:BRAND.blue,d:"0.6s"}].map((b,i)=>(
                    <div key={i} style={{width:9,height:32,borderRadius:5,background:b.c,animation:"bounce 0.9s ease-in-out infinite",animationDelay:b.d}}/>
                  ))}
                </div>
                <div style={{fontSize:15,fontWeight:600,color:theme==="dark"?"#fff":"#1a1a1a"}}>{sendStep||"Sending..."}</div>
                <div style={{fontSize:12,color:theme==="dark"?"rgba(255,255,255,0.45)":"rgba(0,0,0,0.4)"}}>Please wait, this may take a few seconds</div>
              </div>}
            </div>
            <div style={{background:t.bg,borderRadius:12,padding:20,border:`1px solid ${t.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:14,color:t.text}}>Tax Return — {client.taxYear}</div>
                {taxReturnUrl?<span style={{...sp("Completed",theme),fontSize:11}}>✓ Return on File</span>:<span style={{...sp("Not Started",theme),fontSize:11}}>No Return Yet</span>}
              </div>
              {taxReturnUrl&&(
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,background:theme==="dark"?"#0e2818":"#edfaf0",borderRadius:8,padding:"10px 14px",border:`1px solid ${BRAND.green}30`}}>
                  <Icon name="file" size={18} color={BRAND.green}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:t.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{taxReturnFile||"Tax Return"}</div>
                    <div style={{fontSize:11,color:t.textMuted}}>Tax Year {client.taxYear}</div>
                  </div>
                  <a href={taxReturnUrl} target="_blank" rel="noopener noreferrer" style={{...btnP,padding:"7px 14px",fontSize:12,textDecoration:"none"}}>
                    <Icon name="eye" size={13} color="#fff"/>View Return
                  </a>
                </div>
              )}
              <div style={{border:`2px dashed ${t.border}`,borderRadius:10,padding:20,textAlign:"center"}}>
                <input ref={fileInputRef} type="file" accept=".pdf" style={{display:"none"}} onChange={handleTaxReturnUpload}/>
                <Icon name="upload" size={24} color={t.textMuted}/>
                <div style={{fontSize:12.5,color:t.textSub,fontWeight:600,marginTop:8,marginBottom:4}}>{taxReturnUrl?"Replace Tax Return":"Upload Tax Return"}</div>
                <div style={{fontSize:11,color:t.textMuted,marginBottom:12}}>Drake format: <span style={{fontFamily:"monospace",fontSize:10.5}}>{client.taxYear} Tax Return Documents ({(client.name||"CLIENT NAME").toUpperCase()} - Preparer Copy).pdf</span></div>
                {uploadProgress!==null&&(
                  <div style={{marginBottom:10}}>
                    <div style={{height:4,background:t.border,borderRadius:2,overflow:"hidden",marginBottom:6}}><div style={{height:"100%",width:`${uploadProgress}%`,background:BRAND.blue,borderRadius:2,transition:"width 0.2s"}}/></div>
                    <div style={{fontSize:11,color:BRAND.blue}}>{uploadProgress}%</div>
                  </div>
                )}
                {uploadError&&<div style={{fontSize:12,color:"#f87171",marginBottom:8}}>{uploadError}</div>}
                {uploadSuccess&&<div style={{fontSize:12,color:BRAND.green,marginBottom:8}}>{uploadSuccess}</div>}
                <button onClick={()=>fileInputRef.current?.click()} disabled={uploadProgress!==null&&uploadProgress<100} style={{...btnP,margin:"0 auto"}}>
                  <Icon name="upload" size={13} color="#fff"/>Choose PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {tab==="businesses"&&(
          <BusinessesTab client={client} theme={theme} allClients={allClients} onUpdate={onUpdate}/>
        )}

        {tab==="invoice"&&(
          <InvoiceTab client={client} onUpdate={onUpdate} theme={theme}/>
        )}

        {tab==="emails"&&(
          <ClientEmailsTab client={client} theme={theme}/>
        )}

        {tab==="history"&&(
          <div>
            {/* ── Start New Tax Year Panel ─────────────────────────────── */}
            <div style={{background:t.bg,border:`1px solid ${rolloverUnlocked?BRAND.blue+"40":"#f59e0b40"}`,borderRadius:14,padding:20,marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <div style={{fontSize:15,fontWeight:700,color:t.text}}>Start {nextYear} Tax Year</div>
                {!rolloverUnlocked&&(
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,background:"#faeeda",color:"#854f0b",border:"1px solid #fac77560"}}>
                    🔒 Available Oct 19
                  </span>
                )}
              </div>
              <div style={{fontSize:12,color:t.textMuted,marginBottom:14,lineHeight:1.6}}>
                This will archive the <strong style={{color:t.text}}>{client.taxYear}</strong> engagement and open a fresh pipeline for <strong style={{color:t.text}}>{nextYear}</strong>.<br/>
                <span style={{color:"#f59e0b"}}>⚠ What resets:</span> Status, Engagement Sent/Signed, DocuSeal submission.<br/>
                <span style={{color:BRAND.green}}>✓ What stays:</span> Client info, QuickBooks link, OneDrive link, businesses, fees, assigned staff.
              </div>
              {rolloverUnlocked?(
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <input
                    value={yearConfirm}
                    onChange={e=>setYearConfirm(e.target.value)}
                    placeholder={`Type ${nextYear} to confirm`}
                    style={{...{background:t.inputBg||t.bg,border:`1px solid ${yearConfirm===nextYear?BRAND.green:t.border}`,borderRadius:8,padding:"8px 12px",color:t.text,fontSize:13,outline:"none",fontFamily:"'Outfit',sans-serif",width:160}}}
                  />
                  <button onClick={handleStartNewYear} disabled={startingYear||yearConfirm!==nextYear}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"8px 18px",borderRadius:9,
                      background:yearConfirm===nextYear?`linear-gradient(135deg,${BRAND.blue},#1a8bbf)`:"#ccc",
                      border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:yearConfirm===nextYear?"pointer":"not-allowed",
                      fontFamily:"'Outfit',sans-serif",opacity:yearConfirm===nextYear?1:0.6}}>
                    {startingYear?<><MiniSpinner size={12} color="#fff"/>Starting...</>:`🚀 Start ${nextYear} Tax Year`}
                  </button>
                </div>
              ):(
                <div style={{fontSize:12,color:"#f59e0b",fontWeight:600,background:"#faeeda",padding:"8px 14px",borderRadius:8,display:"inline-block"}}>
                  This button unlocks on October 19, {now.getFullYear()+( now.getMonth()>=9&&now.getDate()>=19?1:0)} when the new filing season begins.
                </div>
              )}
            </div>

            {/* ── Engagement History ───────────────────────────────────── */}
            <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:14,padding:20}}>
              <div style={{fontWeight:700,fontSize:14,color:t.text,marginBottom:14}}>Engagement History</div>
              {(client.engagementHistory||[]).length===0?(
                <div style={{fontSize:12.5,color:t.textMuted,textAlign:"center",padding:"20px 0"}}>No archived years yet. History will appear here after the first rollover.</div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {[...(client.engagementHistory||[])].sort((a,b)=>b.year-a.year).map(h=>(
                    <div key={h.year} style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <span style={{fontSize:16,fontWeight:800,color:BRAND.blue}}>{h.year}</span>
                        <span style={{...sp(h.status||"Completed",theme),fontSize:11}}>{h.status||"Completed"}</span>
                        {h.engagementSigned&&<span style={{fontSize:10,fontWeight:700,color:BRAND.green,background:BRAND.green+"15",padding:"2px 7px",borderRadius:8}}>✍ Signed</span>}
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {h.signedAt&&<span style={{fontSize:11,color:t.textMuted}}>Signed {new Date(h.signedAt?.seconds?h.signedAt.seconds*1000:h.signedAt).toLocaleDateString()}</span>}
                        {h.signedEngagementUrl?(
                          <a href={h.signedEngagementUrl} target="_blank" rel="noopener noreferrer"
                            style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:8,background:BRAND.blue+"15",border:`1px solid ${BRAND.blue}30`,color:BRAND.blue,fontSize:12,fontWeight:600,textDecoration:"none"}}>
                            <Icon name="eye" size={12} color={BRAND.blue}/>View
                          </a>
                        ):(
                          <span style={{fontSize:11,color:t.textMuted,fontStyle:"italic"}}>No signed copy</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
}


// Clients Page
